import { useMutation } from '@tanstack/react-query';
import { CalendarCheck2, MapPin, Wand2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { formatDuration } from '@/features/job-types/job-types-api';
import { addDays, local, scheduleApi, type Slot, type Suggestions } from '@/features/schedule/schedule-api';
import { ApiError } from '@/lib/api';
import { intlLocale } from '@/lib/i18n';
import { useTrade } from '@/lib/trade';
import { cn } from '@/lib/utils';

/**
 * שיבוץ בזמן יצירת משימה: מתי אפשר לבצע (מיד, או אחרי הכנה — ייצור,
 * הזמנת חלקים), כמה זמן זה לוקח, והצעות מועדים לפי שעות העבודה, החופשים,
 * המרחק מהעבודה הקודמת והכישור. בלי בחירה — השיבוץ האוטומטי ברקע, כמו קודם.
 */

export interface PickedSlot {
  userId: string;
  userName: string;
  start: string;
  end: string;
}

export interface Readiness {
  /** null = אפשר מיד. */
  readyFrom: string | null;
}

const DURATIONS = [30, 45, 60, 90, 120, 180, 240, 300, 360, 480, 600];

const slotDay = new Intl.DateTimeFormat(intlLocale(), { timeZone: 'Asia/Jerusalem', weekday: 'long', day: 'numeric', month: 'numeric' });
const slotTime = new Intl.DateTimeFormat(intlLocale(), { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit' });

export function formatSlot(s: { start: string; end: string }): string {
  return `${slotDay.format(new Date(s.start))}, ${slotTime.format(new Date(s.start))}–${slotTime.format(new Date(s.end))}`;
}

export function SlotPicker({
  customerId,
  jobType,
  value,
  onChange,
  onReadinessChange,
}: {
  customerId: string;
  jobType: { id: string; durationMinutes: number; leadTimeDays: number };
  value: PickedSlot | null;
  onChange: (slot: PickedSlot | null) => void;
  onReadinessChange: (r: Readiness) => void;
}) {
  const { t } = useTranslation('tasks');
  const trade = useTrade();
  const today = local(new Date()).date;
  const [needsPrep, setNeedsPrep] = useState(jobType.leadTimeDays > 0);
  const [readyFrom, setReadyFrom] = useState(addDays(today, jobType.leadTimeDays || 1));
  const [duration, setDuration] = useState(jobType.durationMinutes);
  const [result, setResult] = useState<Suggestions | null>(null);

  // סוג עבודה אחר → ברירות המחדל שלו.
  useEffect(() => {
    setNeedsPrep(jobType.leadTimeDays > 0);
    setReadyFrom(addDays(today, jobType.leadTimeDays || 1));
    setDuration(jobType.durationMinutes);
  }, [jobType.id, jobType.leadTimeDays, jobType.durationMinutes, today]);

  // כל שינוי בנתונים מבטל הצעות ישנות — הן חושבו על משהו אחר.
  useEffect(() => {
    setResult(null);
    onChange(null);
    onReadinessChange({ readyFrom: needsPrep ? readyFrom : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the inputs change, not when the parent's callbacks do
  }, [customerId, jobType.id, needsPrep, readyFrom, duration]);

  const suggest = useMutation({
    mutationFn: () =>
      scheduleApi.suggest({
        customerId,
        jobTypeTemplateId: jobType.id,
        durationMinutes: duration,
        readyFrom: needsPrep ? readyFrom : today,
      }),
    onSuccess: setResult,
  });

  return (
    <section className="space-y-3 rounded-(--radius-lg) border border-border bg-surface-sunken p-3" aria-label={t('slot.section')}>
      <div className="flex items-center gap-2">
        <CalendarCheck2 className="size-4 text-accent" aria-hidden />
        <h3 className="text-sm font-medium text-fg">{t('slot.section')}</h3>
      </div>

      <fieldset>
        <legend className="mb-1.5 text-xs font-medium text-fg">{t('slot.whenLegend')}</legend>
        <div className="grid grid-cols-2 gap-2" role="radiogroup">
          {[
            { prep: false, label: t('slot.now'), hint: t('slot.nowHint') },
            { prep: true, label: t('slot.prep'), hint: t('slot.prepHint') },
          ].map((o) => (
            <button
              key={String(o.prep)}
              type="button"
              role="radio"
              aria-checked={needsPrep === o.prep}
              onClick={() => setNeedsPrep(o.prep)}
              className={cn(
                'rounded-(--radius-md) border px-3 py-2 text-start transition-colors',
                needsPrep === o.prep ? 'border-accent bg-accent-subtle' : 'border-border bg-surface hover:border-border-strong',
              )}
            >
              <span className="block text-sm font-medium text-fg">{o.label}</span>
              <span className="block text-2xs text-fg-muted">{o.hint}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-2">
        {needsPrep ? (
          <Field label={t('slot.readyFrom')} htmlFor="ready-from">
            <Input id="ready-from" type="date" dir="ltr" min={today} value={readyFrom} onChange={(e) => setReadyFrom(e.target.value || today)} />
          </Field>
        ) : null}
        <Field label={t('slot.duration')} htmlFor="slot-duration">
          <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
            <SelectTrigger id="slot-duration" aria-label={t('slot.duration')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...new Set([...DURATIONS, duration])]
                .sort((a, b) => a - b)
                .map((m) => (
                  <SelectItem key={m} value={String(m)}>
                    {formatDuration(m)}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Button type="button" variant="secondary" size="sm" loading={suggest.isPending} onClick={() => suggest.mutate()}>
        <Wand2 aria-hidden />
        {t('slot.suggest')}
      </Button>
      {suggest.isError ? (
        <p role="alert" className="text-xs text-danger">
          {suggest.error instanceof ApiError ? suggest.error.message : t('slot.suggestFailed')}
        </p>
      ) : null}

      {result ? (
        <div className="space-y-2" aria-live="polite">
          {!result.customerLocated ? (
            <p className="flex items-center gap-1.5 text-2xs text-fg-muted">
              <MapPin className="size-3" aria-hidden />
              {t('slot.noLocation')}
            </p>
          ) : null}
          {result.skillFallback ? (
            <p className="text-2xs text-warning">{t('slot.skillFallback', { worker: trade.worker.one })}</p>
          ) : null}
          <ul className="space-y-1.5" role="radiogroup" aria-label={t('slot.suggestedSlots')}>
            <SlotOption
              checked={value === null}
              onSelect={() => onChange(null)}
              title={t('slot.noSlot')}
              detail={t('slot.noSlotDetail', { worker: trade.worker.one })}
            />
            {result.slots.map((s) => (
              <SlotOption
                key={`${s.userId}-${s.start}`}
                checked={value?.userId === s.userId && value.start === s.start}
                onSelect={() => onChange(pick(s))}
                title={`${s.userName} · ${formatSlot(s)}`}
                detail={s.reasons.join(' · ')}
              />
            ))}
          </ul>
          {result.slots.length === 0 ? (
            <p className="text-xs text-fg-muted">{t('slot.noWindow')}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

const pick = (s: Slot): PickedSlot => ({ userId: s.userId, userName: s.userName, start: s.start, end: s.end });

function SlotOption({ checked, onSelect, title, detail }: { checked: boolean; onSelect: () => void; title: string; detail: string }) {
  return (
    <li>
      <button
        type="button"
        role="radio"
        aria-checked={checked}
        onClick={onSelect}
        className={cn(
          'flex w-full items-start gap-2.5 rounded-(--radius-md) border px-3 py-2 text-start transition-colors',
          checked ? 'border-accent bg-accent-subtle' : 'border-border bg-surface hover:border-border-strong',
        )}
      >
        <span
          aria-hidden
          className={cn('mt-1 size-3.5 shrink-0 rounded-full border-2', checked ? 'border-accent bg-accent' : 'border-border-strong')}
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-fg">{title}</span>
          <span className="block text-2xs text-fg-muted">{detail}</span>
        </span>
      </button>
    </li>
  );
}
